import { OpenAPI } from "../generated/api";
import { ApiError } from "../generated/api/core/ApiError";

export * from "../generated/api";

export type AuthTokenProvider = () => string | null | Promise<string | null>;
export type UnauthorizedHandler = () => void | Promise<void>;

let tokenProvider: AuthTokenProvider | null = null;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export const configureApiClient = (options?: {
  baseUrl?: string;
  tokenProvider?: AuthTokenProvider;
  onUnauthorized?: UnauthorizedHandler;
}) => {
  const baseUrl =
    options?.baseUrl ??
    (process.env.NODE_ENV === "production" ? process.env.REACT_APP_API_URL || "" : "");

  OpenAPI.BASE = baseUrl;

  if (options?.tokenProvider) {
    tokenProvider = options.tokenProvider;
  }
  if (options?.onUnauthorized) {
    unauthorizedHandler = options.onUnauthorized;
  }

  OpenAPI.TOKEN = async () => (await tokenProvider?.()) ?? "";
};

export const initializeApiClientAuth = (options: {
  tokenProvider?: AuthTokenProvider;
  onUnauthorized?: UnauthorizedHandler;
}) => {
  if (options.tokenProvider) {
    tokenProvider = options.tokenProvider;
  }
  if (options.onUnauthorized) {
    unauthorizedHandler = options.onUnauthorized;
  }
};

let isConfigured = false;

const ensureApiClientConfigured = () => {
  if (isConfigured) {
    return;
  }

  configureApiClient();
  isConfigured = true;
};

const parseApiErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ApiError) {
    const body = error.body as
      | { detail?: string; message?: string; Message?: string }
      | string
      | undefined;

    if (typeof body === "string" && body.trim()) {
      return body;
    }
    if (body && typeof body === "object") {
      if (typeof body.detail === "string" && body.detail.trim()) {
        return body.detail;
      }
      if (typeof body.message === "string" && body.message.trim()) {
        return body.message;
      }
      if (typeof body.Message === "string" && body.Message.trim()) {
        return body.Message;
      }
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

export const callApi = async <T>(
  action: () => Promise<T>,
  fallbackErrorMessage: string
): Promise<T> => {
  ensureApiClientConfigured();

  try {
    return await action();
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      await unauthorizedHandler?.();
      throw new Error("Session expired. Please login again.");
    }

    const normalizedError = new Error(
      parseApiErrorMessage(error, fallbackErrorMessage)
    ) as Error & { status?: number; body?: unknown };
    if (error instanceof ApiError) {
      normalizedError.status = error.status;
      normalizedError.body = error.body;
    }
    throw normalizedError;
  }
};

export const fetchWithApiClient = async (
  path: string,
  init?: RequestInit
): Promise<Response> => {
  ensureApiClientConfigured();

  const token = (await tokenProvider?.()) ?? "";
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url =
    /^https?:\/\//i.test(path) ? path : `${OpenAPI.BASE}${normalizedPath}`;

  const headers = new Headers(init?.headers);
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(url, {
    ...init,
    headers,
  });

  if (response.status === 401) {
    await unauthorizedHandler?.();
  }

  return response;
};
